import type { Entity } from '@backstage/catalog-model';
import type { BreadcrumbDefinition } from '@ipen-portal/plugin-navigation-react';
import type { FC, PropsWithChildren, ReactElement, ReactNode } from 'react';

import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';
import { SidebarPinStateProvider } from '@backstage/core-components';
import {
  IpenThemeProvider,
  MobileViewProvider,
  ObservedScrollArea,
  ScrollProvider,
} from '@ipen-portal/plugin-common-react';
import { BreadcrumbProvider } from '@ipen-portal/plugin-navigation-react';
import { queryClient } from '@ipen-portal/plugin-trpc-react';
import { AppBar } from '@ipen/components';
import { QueryClientProvider } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocalStorage } from 'react-use';
import { tss } from 'tss-react/mui';

const useStyles = tss.create(({ theme }) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'auto',
  },
  page: {
    flex: 1,
    overflow: 'auto',
  },
  pageWithNavigation: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
}));

export const Root: FC<
  PropsWithChildren<{
    breadcrumbDefinitions: BreadcrumbDefinition<Entity>[];
    headerComponents: ReactNode;
    navigation: ReactElement;
  }>
> = ({ breadcrumbDefinitions, children, headerComponents, navigation }) => {
  const { classes } = useStyles();

  const [sidebarPinned, setSidebarPinned] = useLocalStorage(
    'sidebarPinState',
    false,
  );

  const toggleSidebarPinState = useCallback(
    () => setSidebarPinned(!sidebarPinned),
    [setSidebarPinned, sidebarPinned],
  );

  return (
    <IpenThemeProvider themeId="default">
      <QueryClientProvider client={queryClient}>
        <SidebarPinStateProvider
          value={{
            isPinned: !!sidebarPinned,
            toggleSidebarPinState,
          }}
        >
          <ScrollProvider stickAt={56}>
            <BreadcrumbProvider breadcrumbDefinitions={breadcrumbDefinitions}>
              <MobileViewProvider>
                <div className={classes.body}>
                  <NewAnnouncementBanner cardOptions={{}} />
                  <AppBar>{headerComponents}</AppBar>
                  <div className={classes.pageWithNavigation}>
                    {navigation}
                    <ObservedScrollArea className={classes.page}>
                      {children}
                    </ObservedScrollArea>
                  </div>
                </div>
              </MobileViewProvider>
            </BreadcrumbProvider>
          </ScrollProvider>
        </SidebarPinStateProvider>
      </QueryClientProvider>
    </IpenThemeProvider>
  );
};
